import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import workOrderService, {
  WorkOrder,
  WorkOrderLineItem,
  ConversionData,
  WorkOrderExecutionScope,
  WorkOrderDeviationType,
  MaterialUtilizationLineInput,
  BoqBomLineOption,
  ProductionReceiptRow,
} from '../services/workOrder.service';

/**
 * Hook to fetch all work orders
 */
export const useWorkOrders = (filters?: any): UseQueryResult<WorkOrder[], Error> => {
  return useQuery({
    queryKey: ['work-orders', filters],
    queryFn: async () => {
      const data = await workOrderService.getAll(filters);
      console.log('Work Orders fetched:', data);
      console.log('Number of work orders:', data?.length || 0);
      return data;
    }
  });
};

/**
 * Hook to fetch a single work order
 */
export const useWorkOrder = (id: string): UseQueryResult<WorkOrder, Error> => {
  return useQuery({
    queryKey: ['work-orders', id],
    queryFn: () => workOrderService.getById(id),
    enabled: !!id
  });
};

/** Finished goods receipts posted against a work order (inventory traceability). */
export const useWorkOrderProductionReceipts = (
  workOrderId: string,
  options?: { enabled?: boolean }
): UseQueryResult<ProductionReceiptRow[], Error> => {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'production-receipts'],
    queryFn: () => workOrderService.getProductionReceipts(workOrderId),
    enabled: options?.enabled !== false && !!workOrderId,
  });
};

export const useBoqBomLineOptions = (
  workOrderId: string,
  options?: { enabled?: boolean }
): UseQueryResult<BoqBomLineOption[], Error> => {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'boq-bom-line-options'],
    queryFn: () => workOrderService.getBoqBomLineOptions(workOrderId),
    enabled: options?.enabled !== false && !!workOrderId,
  });
};

/**
 * Resolve a single line item from the parent work order (no dedicated line-item API).
 */
export const useWorkOrderLineItem = (
  lineItemId: string,
  workOrderId: string
): UseQueryResult<WorkOrderLineItem | undefined, Error> => {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'line-item', lineItemId],
    queryFn: async () => {
      const wo = await workOrderService.getById(workOrderId);
      return wo.lineItems?.find((li) => li.id === lineItemId);
    },
    enabled: !!lineItemId && !!workOrderId
  });
};

/**
 * Hook to create a new work order
 */
export const useCreateWorkOrder = (): UseMutationResult<WorkOrder, Error, Partial<WorkOrder>> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data: Partial<WorkOrder>) => workOrderService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      message.success('Work Order created successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create Work Order');
    }
  });
};

/**
 * Hook to update a work order
 */
export type WorkOrderUpdateData = Omit<Partial<WorkOrder>, 'materialUtilizationLines'> & {
  executionScope?: WorkOrderExecutionScope;
  deviationType?: WorkOrderDeviationType;
  clientScopeOfWork?: string | null;
  clientQualityRequirements?: string | null;
  wo_customer_requirements_notes?: string | null;
  productionSupervisorUserId?: string | null;
  qualityEngineerUserId?: string | null;
  materialUtilizationLines?: MaterialUtilizationLineInput[];
};

export const useUpdateWorkOrder = (): UseMutationResult<
  WorkOrder,
  Error,
  { id: string; data: WorkOrderUpdateData }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: WorkOrderUpdateData }) =>
      workOrderService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', variables.id] });
      message.success('Work Order updated successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to update Work Order');
    }
  });
};

/**
 * Hook to release a work order
 */
export const useReleaseWorkOrder = (): UseMutationResult<WorkOrder, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => workOrderService.release(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', id] });
      message.success('Work Order released successfully');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to release Work Order');
    }
  });
};

/**
 * Hook to start a work order
 */
export const useStartWorkOrder = (): UseMutationResult<WorkOrder, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => workOrderService.start(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', id] });
      message.success('Work Order started successfully');
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message ?? error?.response?.data?.error ?? error?.message ?? 'Failed to start Work Order';
      message.error(msg);
    }
  });
};

/**
 * Hook to complete a work order
 */
export const useCompleteWorkOrder = (): UseMutationResult<WorkOrder, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => workOrderService.complete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', id] });
      message.success('Work Order completed successfully');
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message ?? error?.response?.data?.error ?? error?.message ?? 'Failed to complete Work Order';
      message.error(msg);
    }
  });
};

/**
 * Hook to release work order after inspection
 */
export const useReleaseAfterInspection = (): UseMutationResult<WorkOrder, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => workOrderService.releaseAfterInspection(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', id] });
      message.success('Work Order released after inspection');
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to release Work Order');
    }
  });
};

/**
 * Hook to get work order inspections
 */
export const useWorkOrderInspections = (workOrderId: string): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: ['work-orders', workOrderId, 'inspections'],
    queryFn: () => workOrderService.getInspections(workOrderId),
    enabled: !!workOrderId
  });
};

/**
 * Hook to convert sales order to work orders
 */
export const useConvertSalesOrderToWorkOrder = (): UseMutationResult<
  WorkOrder[],
  Error,
  { salesOrderId: string; data: ConversionData }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ salesOrderId, data }: { salesOrderId: string; data: ConversionData }) =>
      workOrderService.convertFromSalesOrder(salesOrderId, data),
    onSuccess: (workOrders, variables) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders'] });
      queryClient.invalidateQueries({ queryKey: ['sales-orders', variables.salesOrderId] });
      const count = workOrders?.length || 0;
      message.success({
        content: `Successfully created ${count} work order(s)! Opening the work orders list…`,
        duration: 6
      });
    },
    onError: (error: Error) => {
      const err = error as any;
      const apiMessage = err?.response?.data?.message;
      const validation = err?.response?.data?.errors;
      const issues = err?.response?.data?.issues;
      const issuesStr =
        Array.isArray(issues) && issues.length
          ? ` Details: ${issues.map((i: { path?: unknown[]; message?: string }) => `${(i.path || []).join('.')}: ${i.message}`).join('; ')}`
          : '';
      const extra =
        validation && typeof validation === 'object'
          ? ` ${JSON.stringify(validation)}`
          : '';
      message.error(
        (apiMessage ? `${apiMessage}${extra}${issuesStr}` : null) ||
          error.message ||
          'Failed to convert Customer Order to Work Orders'
      );
    }
  });
};

/**
 * Hook to convert production request to work orders (same conversion form as sales order).
 */
export const useConvertProductionRequestToWorkOrder = (): UseMutationResult<
  WorkOrder[],
  Error,
  { productionRequestId: string; data: ConversionData }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ productionRequestId, data }: { productionRequestId: string; data: ConversionData }) =>
      workOrderService.convertFromProductionRequest(productionRequestId, data),
    onSuccess: (workOrders, variables) => {
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['production-requests'] });
      queryClient.invalidateQueries({ queryKey: ['production-request', variables.productionRequestId] });
      const count = workOrders?.length || 0;
      message.success({
        content: `Successfully created ${count} work order(s)! Opening the work orders list…`,
        duration: 6,
      });
    },
    onError: (error: Error) => {
      const err = error as any;
      const apiMessage = err?.response?.data?.message;
      const validation = err?.response?.data?.errors;
      const issues = err?.response?.data?.issues;
      const issuesStr =
        Array.isArray(issues) && issues.length
          ? ` Details: ${issues.map((i: { path?: unknown[]; message?: string }) => `${(i.path || []).join('.')}: ${i.message}`).join('; ')}`
          : '';
      const extra =
        validation && typeof validation === 'object'
          ? ` ${JSON.stringify(validation)}`
          : '';
      message.error(
        (apiMessage ? `${apiMessage}${extra}${issuesStr}` : null) ||
          error.message ||
          'Failed to convert Production Request to Work Orders'
      );
    },
  });
};
